import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Spinner } from './ui/spinner';
import { useToast } from './ui/toast';
import { ArrowRight, RefreshCw } from 'lucide-react';
import api from '../services/api';

const statusLabels = {
  assigned: 'Хүргэлтэнд', delivered: 'Хүргэгдсэн', failed: 'Цуцалсан', transferred: 'Шилжүүлсэн',
  postponed: 'Хойшлогдсон', tomorrow: 'Маргааш авах', phone_not_answered: 'Утас авахгүй',
  cannot_contact: 'Холбогдохгүй', wrong_phone: 'Утасны дугаар буруу', unclear_address: 'Хаяг тодорхойгүй',
  gate_locked: 'Хотхоны хаалт', entrance_chip: 'Орц байрны чип', no_one_home: 'Хаяг хүнгүй', other: 'Бусад',
};
const statusColors = {
  assigned: 'info', delivered: 'success', failed: 'destructive', transferred: 'secondary',
  postponed: 'warning', tomorrow: 'warning', phone_not_answered: 'warning',
  cannot_contact: 'warning', wrong_phone: 'destructive', unclear_address: 'destructive',
  gate_locked: 'warning', entrance_chip: 'warning', no_one_home: 'destructive', other: 'secondary',
};

const Transfers = () => {
  const [drivers, setDrivers] = useState([]);
  const [deliveriesByDriver, setDeliveriesByDriver] = useState({});
  const [loading, setLoading] = useState(false);
  const [transferring, setTransferring] = useState(false);
  // { driverId -> Set of deliveryIds }
  const [selected, setSelected] = useState({});
  const { toast } = useToast();

  const fetchDrivers = async () => {
    try {
      const res = await api.get('/drivers');
      setDrivers(res.data.filter(d => d.isActive));
    } catch {}
  };

  const fetchAllDeliveries = async () => {
    setLoading(true);
    try {
      const res = await api.get('/deliveries', { params: { activeOnly: 'true', limit: 1000 } });
      const grouped = {};
      for (const d of (res.data.deliveries || [])) {
        const driverId = d.driver?._id;
        if (!driverId) continue;
        if (!grouped[driverId]) grouped[driverId] = [];
        grouped[driverId].push(d);
      }
      setDeliveriesByDriver(grouped);
    } catch {
      toast({ title: 'Хүргэлт татахад алдаа гарлаа', variant: 'error' });
    }
    setLoading(false);
  };

  useEffect(() => { fetchDrivers(); fetchAllDeliveries(); }, []);

  const doneStatuses = ['delivered', 'failed', 'transferred', 'returned'];

  const getActive = (driverId) =>
    (deliveriesByDriver[driverId] || []).filter(d => !doneStatuses.includes(d.status));

  const getDone = (driverId) =>
    (deliveriesByDriver[driverId] || []).filter(d => d.status !== 'delivered' && ['failed', 'transferred', 'returned'].includes(d.status));

  const getSelectedIds = (driverId) => selected[driverId] || new Set();

  const toggleSelect = (driverId, deliveryId) => {
    setSelected(prev => {
      const set = new Set(prev[driverId] || []);
      if (set.has(deliveryId)) set.delete(deliveryId);
      else set.add(deliveryId);
      return { ...prev, [driverId]: set };
    });
  };

  const selectAll = (driverId) => {
    const active = getActive(driverId);
    const current = getSelectedIds(driverId);
    const allSelected = active.length > 0 && active.every(d => current.has(d._id));
    if (allSelected) {
      setSelected(prev => ({ ...prev, [driverId]: new Set() }));
    } else {
      setSelected(prev => ({ ...prev, [driverId]: new Set(active.map(d => d._id)) }));
    }
  };

  const handleTransfer = async (fromDriverId, toDriverId) => {
    const ids = [...getSelectedIds(fromDriverId)];
    if (ids.length === 0) return;

    setTransferring(true);
    let success = 0;
    let errors = 0;

    for (const deliveryId of ids) {
      try {
        await api.post(`/deliveries/${deliveryId}/transfer`, {
          toDriverId,
          reason: 'Админ шилжүүлэв',
        });
        success++;
      } catch {
        errors++;
      }
    }

    toast({
      title: `${success} хүргэлт шилжүүлэгдлээ${errors > 0 ? `, ${errors} алдаатай` : ''}`,
      variant: errors > 0 ? 'warning' : 'success',
    });

    setSelected({});
    setTransferring(false);
    fetchAllDeliveries();
  };

  // Аль баганаас сонголт хийгдсэн бэ
  const activeFromDriver = Object.entries(selected).find(([, set]) => set.size > 0);
  const fromDriverId = activeFromDriver?.[0];
  const totalSelected = activeFromDriver ? activeFromDriver[1].size : 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">Шилжүүлэх</h2>
        <Button variant="outline" size="sm" onClick={() => { fetchAllDeliveries(); setSelected({}); }}>
          <RefreshCw className="h-4 w-4 mr-2" />Шинэчлэх
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Spinner /></div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: 'calc(100vh - 180px)' }}>
          {drivers.map(driver => {
            const active = getActive(driver._id);
            const done = getDone(driver._id);
            const selectedIds = getSelectedIds(driver._id);
            const allSelected = active.length > 0 && active.every(d => selectedIds.has(d._id));
            const isFrom = fromDriverId === driver._id;
            const isTarget = fromDriverId && fromDriverId !== driver._id;

            return (
              <div key={driver._id}
                className={`flex-shrink-0 w-72 border rounded-lg bg-white flex flex-col ${
                  isFrom ? 'ring-2 ring-primary' : isTarget ? 'ring-2 ring-green-300' : ''
                }`}>
                {/* Header */}
                <div className="p-3 border-b bg-zinc-50 rounded-t-lg">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-sm">{driver.name}</span>
                    <Badge variant="info" className="text-xs">{active.length}</Badge>
                  </div>
                  <div className="text-xs text-zinc-400 mt-0.5">{driver.phone}</div>
                  <div className="flex gap-1.5 mt-2">
                    <span className="text-xs text-primary bg-primary/10 px-1.5 py-0.5 rounded">{active.length} хүргэх</span>
                    <span className="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded">{done.filter(d => d.status === 'delivered').length} хүргэсэн</span>
                  </div>
                </div>

                {/* Transfer target button */}
                {isTarget && totalSelected > 0 && (
                  <button
                    disabled={transferring}
                    onClick={() => handleTransfer(fromDriverId, driver._id)}
                    className="mx-2 mt-2 px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                  >
                    <ArrowRight className="h-4 w-4" />
                    {transferring ? 'Шилжүүлж байна...' : `${totalSelected} хүргэлт авах`}
                  </button>
                )}

                {/* Select all */}
                {active.length > 0 && !isTarget && (
                  <div className="px-3 py-2 border-b flex items-center gap-2">
                    <input type="checkbox" checked={allSelected}
                      onChange={() => selectAll(driver._id)} className="rounded" />
                    <span className="text-xs text-zinc-500">
                      Бүгдийг сонгох {selectedIds.size > 0 && `(${selectedIds.size})`}
                    </span>
                  </div>
                )}

                {/* Delivery list */}
                <div className="flex-1 overflow-y-auto">
                  {active.length === 0 && done.length === 0 ? (
                    <div className="p-4 text-center text-zinc-400 text-xs">Хүргэлт байхгүй</div>
                  ) : (
                    <div className="divide-y">
                      {active.map(d => (
                        <div key={d._id}
                          className={`flex items-start gap-2 px-3 py-2 hover:bg-zinc-50 cursor-pointer transition-colors ${
                            selectedIds.has(d._id) ? 'bg-primary/10' : ''
                          } ${isTarget ? 'pointer-events-none opacity-60' : ''}`}
                          onClick={() => !isTarget && toggleSelect(driver._id, d._id)}>
                          {!isTarget && (
                            <input type="checkbox" checked={selectedIds.has(d._id)} readOnly
                              className="rounded mt-0.5 shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-semibold">{d.order?.orderNumber}</span>
                              <Badge variant={statusColors[d.status]} className="text-[10px] px-1.5 py-0">{statusLabels[d.status]}</Badge>
                            </div>
                            <div className="text-[11px] text-zinc-500 truncate mt-0.5">
                              {d.order?.district?.name}{d.order?.khoroo ? ` ${d.order.khoroo}-р` : ''}
                            </div>
                            <div className="text-[11px] text-zinc-400 truncate">{d.order?.address}</div>
                            <div className="text-[11px] text-zinc-400">{d.order?.phone1}</div>
                          </div>
                        </div>
                      ))}
                      {done.length > 0 && (
                        <>
                          <div className="px-3 py-1 bg-zinc-100 text-[10px] text-zinc-500 font-medium sticky top-0">
                            Дууссан ({done.length})
                          </div>
                          {done.map(d => (
                            <div key={d._id} className="flex items-center gap-2 px-3 py-1.5 opacity-40">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[11px]">{d.order?.orderNumber}</span>
                                  <Badge variant={statusColors[d.status]} className="text-[10px] px-1.5 py-0">{statusLabels[d.status]}</Badge>
                                </div>
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Transfers;
